// useFlashDesigns.js
// Shared hook: reads flash designs from Firestore config/flashDesigns
// (+ per-event profiles). Firestore is the SOLE source of truth.
//
// NO hardcoded fallback. The old fallback (src/flashDesigns.js FLASH_DESIGNS,
// pointing at the legacy /flash-designs/*.png art) rendered for the split
// second before onSnapshot resolved, then got swapped for the updated
// Firestore designs of the same characters — a visible flash of stale art on
// every overlay/register load. We now render NOTHING until real data arrives,
// so the only designs ever shown are the current Firestore ones.
//
// Profile resolution: when an activeShift is passed in and it references
// flashDesignProfiles via activeFlashProfileIds[], those profiles' designs
// are fetched and merged into the result. The base set is included unless
// activeShift.includeBaseFlashSet === false. Designs are deduped by id —
// later sources (profiles) win over earlier (base set).
//
// previewWhenIdle (opt-in, overlays only): with NO active shift, fall back to
// config/stream.previewFlashProfileIds / previewIncludeBaseFlashSet — stamped
// by Event Config "Apply" — so overlay previews show the staged designs before
// the shift opens. A live shift always wins. Callers that deliberately stay on
// the base set (CharacterBadge, ArtistQueueView designMap, customer views)
// simply don't pass the flag and are untouched.
//
// Returns { designs, freestyleDesign, allDesigns, designMap, byLabel, loading }

import { useState, useEffect } from "react";
import { db } from "../firebase";
import { doc, onSnapshot, collection, query, where, documentId, getDocs } from "firebase/firestore";

const FALLBACK = []; // intentionally empty — see header note

export default function useFlashDesigns(activeShift, { previewWhenIdle = false } = {}) {
  const [base, setBase]         = useState(null);   // config/flashDesigns designs[]
  const [profileSets, setProfileSets] = useState({});  // { profileId: designs[] }
  const [preview, setPreview]   = useState(null);   // { ids, includeBase } from config/stream
  const [loading, setLoading]   = useState(true);

  // Base set — always subscribed. While the first snapshot is in flight `base`
  // stays null so consumers render NO flash designs (see baseList below) rather
  // than the stale bundled FALLBACK — that bake-in is the previous design set,
  // and flashing it on every overlay boot/scene-change shows outdated, off-brand
  // art for a frame. FALLBACK is reserved for a real error / missing doc, plus a
  // safety timeout so a hung booth connection still ends up with *something*.
  useEffect(() => {
    const safety = setTimeout(() => { setBase(b => (b === null ? FALLBACK : b)); setLoading(false); }, 4000);
    const unsub = onSnapshot(doc(db, "config", "flashDesigns"), snap => {
      clearTimeout(safety);
      if (snap.exists() && Array.isArray(snap.data().designs) && snap.data().designs.length > 0) {
        setBase(snap.data().designs);
      } else {
        setBase(FALLBACK);
      }
      setLoading(false);
    }, () => { clearTimeout(safety); setBase(FALLBACK); setLoading(false); });
    return () => { clearTimeout(safety); unsub(); };
  }, []);

  // Preview stamp — only subscribed when the caller opted in AND no shift is
  // live, so order flows and the deliberate base-set-only consumers never pay
  // for (or react to) it. config/stream is already watched by every overlay,
  // so the SDK shares the underlying listen stream.
  const wantPreview = previewWhenIdle && !activeShift;
  useEffect(() => {
    if (!wantPreview) { setPreview(null); return; }
    const unsub = onSnapshot(doc(db, "config", "stream"), snap => {
      const d = snap.exists() ? snap.data() : {};
      setPreview({
        ids: Array.isArray(d.previewFlashProfileIds) ? d.previewFlashProfileIds : [],
        includeBase: d.previewIncludeBaseFlashSet !== false,
      });
    }, () => setPreview(null));
    return () => unsub();
  }, [wantPreview]);

  // Profile sets — fetched whenever the shift's profile id list changes.
  // One-shot getDocs (not onSnapshot) since profiles change rarely and a
  // listener-per-profile would balloon for shifts with several sets.
  const profileIds = activeShift?.activeFlashProfileIds || (wantPreview && preview?.ids) || [];
  const profileIdsKey = profileIds.join("|");
  useEffect(() => {
    if (profileIds.length === 0) { setProfileSets({}); return; }
    let cancelled = false;
    (async () => {
      try {
        // Firestore "in" supports up to 30 values; chunk if ever exceeded.
        const chunks = [];
        for (let i = 0; i < profileIds.length; i += 30) chunks.push(profileIds.slice(i, i + 30));
        const sets = {};
        for (const chunk of chunks) {
          const snap = await getDocs(query(collection(db, "flashDesignProfiles"), where(documentId(), "in", chunk)));
          snap.docs.forEach(d => { sets[d.id] = (d.data().designs || []); });
        }
        if (!cancelled) setProfileSets(sets);
      } catch (err) {
        console.warn("useFlashDesigns: profile fetch failed", err?.message || err);
        if (!cancelled) setProfileSets({});
      }
    })();
    return () => { cancelled = true; };
  }, [profileIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const includeBase = activeShift
    ? activeShift.includeBaseFlashSet !== false
    : (wantPreview && preview ? preview.includeBase : true);
  // null = still loading → render nothing (avoids stale-FALLBACK flash). Once
  // resolved, `base` is either the live Firestore set or FALLBACK (error/empty).
  const baseList    = (base ?? []);
  const profileList = profileIds.flatMap(id => profileSets[id] || []);

  // Merge: base first (unless excluded), then profile designs. Dedupe by id —
  // later wins. Freestyle is always kept exactly once and bubbles to the end.
  const merged = mergeAndDedupe(includeBase ? baseList : [], profileList);

  const active       = merged.filter(d => d.active);
  const designs      = active.filter(d => !d.isFreestyle);
  const freestyle    = active.find(d => d.isFreestyle) || null;
  const allDesigns   = freestyle ? [...designs, freestyle] : designs;
  const designMap    = Object.fromEntries(merged.map(d => [d.id, d]));
  const byLabel      = Object.fromEntries(merged.map(d => [d.label.toLowerCase().replace(/\s+/g, ""), d]));

  return { designs, freestyleDesign: freestyle, allDesigns, designMap, byLabel, loading };
}

function mergeAndDedupe(baseList, profileList) {
  const out = [];
  const byId = new Map();
  for (const d of [...baseList, ...profileList]) {
    if (!d || !d.id) continue;
    if (byId.has(d.id)) {
      const idx = byId.get(d.id);
      out[idx] = d; // later wins
    } else {
      byId.set(d.id, out.length);
      out.push(d);
    }
  }
  return out;
}
